'use client';

import { PublicSection } from '../../../../components/public/PublicSection';
import { PROJECT_NAME } from '@shared/constants';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../../../../components/ui/carousel';
import { LandingPageCard } from '../components';

/**
 * @group Landing
 * @summary User personas section displaying seven target audience segments
 */
export function UserPersonasSection() {
  const personas = [
    {
      title: 'Young Professionals',
      description: 'Busy careers shouldn\'t mean neglecting health. Quick consultations that fit around demanding schedules, without taking half a day off work. Mental health support for workplace stress, prescription renewals, and preventative care.',
    },
    {
      title: 'Single Mothers',
      description: `Juggling childcare, work, and household responsibilities leaves little time for doctor visits. ${PROJECT_NAME} provides paediatric consultations for children and health support for mothers — all from home, when the children are finally asleep.`,
    },
    {
      title: 'Elderly Chronic Care Patients',
      description: `Managing diabetes, hypertension, or other chronic conditions requires regular medical oversight. For elderly patients who struggle with transport or mobility, ${PROJECT_NAME} brings the doctor to them — with regular check-ins and prescription management.`,
    },
    {
      title: 'Taxi Drivers & Transport Workers',
      description: 'Long, irregular hours make it nearly impossible to attend clinic appointments. Quick consultations during breaks mean taxi drivers can manage their health without losing income or letting down passengers who depend on them.',
    },
    {
      title: 'First-Time Pregnant Mothers',
      description: `Pregnancy brings countless questions and concerns. ${PROJECT_NAME} provides reassurance and medical guidance for expectant mothers, particularly those in areas with limited maternity care access. Ask questions anytime.`,
    },
    {
      title: 'Rural Farmers & Agricultural Workers',
      description: `Living hours away from the nearest clinic shouldn't mean going without medical care. For farmers and rural workers, ${PROJECT_NAME} eliminates the need for day-long journeys to town, providing quality consultations via mobile phone.`,
    },
    {
      title: 'Corporate Employees',
      description: `Companies increasingly offer ${PROJECT_NAME} as an employee benefit. Quick access to medical certificates, management of workplace stress, and preventative care mean healthier employees and less time away from work.`,
    },
  ];

  return (
    <PublicSection 
      heading="Real South Africans, Real Healthcare Needs"
      headingColor="hsl(var(--brand-mint))"
      subheadingColor="hsl(var(--brand-mint))"
      separatorColor="hsl(var(--brand-mint))"
      separatorLineColor="hsl(var(--brand-mint) / 0.4)"
      subheading={`${PROJECT_NAME} serves the full diversity of South African life. Every person deserves access to quality healthcare, regardless of their circumstances, location, or schedule. Here are just a few of the people we're proud to serve every day.`}
      className="bg-brand-teal"
      indented
    >
      <div className="relative max-w-5xl mx-auto px-8 md:px-0">
        <Carousel
          opts={{
            align: 'start',
            loop: true,
            dragFree: true,
          }}
          className=""
        >
          <CarouselContent className="-ml-2 md:-ml-3">
            {personas.map((persona, idx) => (
              <CarouselItem key={idx} className="pl-2 md:pl-3 md:basis-1/2 lg:basis-1/3">
                <LandingPageCard
                  title={persona.title}
                  description={persona.description}
                  className="h-full border border-border shadow-lg"
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="flex h-10 w-10 md:h-16 md:w-16 left-0 md:left-2 -translate-x-0 md:-translate-x-12 bg-background/90 backdrop-blur-sm" />
          <CarouselNext className="flex h-10 w-10 md:h-16 md:w-16 right-0 md:right-2 translate-x-0 md:translate-x-12 bg-background/90 backdrop-blur-sm" />
        </Carousel>
      </div>
    </PublicSection>
  );
}
